import axios from "axios";
import { ApiPathRESE } from "../../../common/api-path/ApiPath";

/**
 * Maps a single raw record from the fetchMonitoringDetails response
 * (snake_case) into the camelCase shape used by the Monitoring Request Logs
 * table/UI.
 */
const mapMonitoringRecord = (record) => ({
    id: record.id,
    division: record.division,
    storeId: record.store_id,
    assetName: record.asset_name,
    materialType: record.material_type,
    chargeSize: record.charge_size,
    monitoringStartDate: record.monitoring_date_start,
    monitoringDuration: record.monitoring_duration,
    notes: record.notes,
    createdBy: record.created_by,
    createdTimestamp: record.created_timestamp,
    modifiedBy: record.modified_by,
    modifiedTimestamp: record.modified_timestamp,
});

/**
 * Fetch the Monitoring Request Logs for the given date range. The endpoint does
 * not support server-side pagination/sorting/filtering - it returns the full
 * set of records for the date range, and the UI paginates/sorts/filters
 * client-side (mirrors ReseAlertSuppressionLogs.jsx).
 */
export const getReseLeakDetectionMonitoringTableData = async (startDatetime, endDatetime, offset) => {
    const requestConfig = {
        params: { startDate: startDatetime, endDate: endDatetime, offset },
        headers: {
            Usecase: "leak-detection",
            Accept: "application/json",
        },
    };
    const response = await axios.get(`${ApiPathRESE.baseUrl}/fetchMonitoringDetails`, requestConfig);
    if (response.status >= 400) {
        throw new Error(response.data?.message || response.data?.error || response.statusText);
    }
    const records = response.data?.data?.records || [];
    return {
        monitoringDataList: records.map(mapMonitoringRecord),
        totalRecords: response.data?.data?.total_records || records.length,
    };
};

/**
 * Fetch the Material Type dropdown options from the Leak Detection
 * Monitoring backend.
 */
export const getReseLeakDetectionMonitoringMaterialType = async () => {
    const response = await axios.get(`${ApiPathRESE.baseUrl}/getMonitoringMaterialType`, {
        headers: {
            Usecase: "leak-detection",
            Accept: "application/json",
        },
    });
    if (response.status >= 400) {
        throw new Error(response.data?.message || response.data?.error || response.statusText);
    }
    return response.data?.data?.material_type || [];
};

/**
 * Fetch the Monitoring Duration dropdown options from the Leak Detection
 * Monitoring backend. Values are coerced to strings since the backend may
 * return numbers (e.g. 30, 60, 90) and PrimeReact's Dropdown type-ahead
 * search requires string labels.
 */
export const getReseLeakDetectionMonitoringDuration = async () => {
    const response = await axios.get(`${ApiPathRESE.baseUrl}/getMonitoringDuration`, {
        headers: {
            Usecase: "leak-detection",
            Accept: "application/json",
        },
    });
    if (response.status >= 400) {
        throw new Error(response.data?.message || response.data?.error || response.statusText);
    }
    const durations = response.data?.data?.monitoring_duration || [];
    return durations.map((duration) => String(duration));
};

/**
 * Submit (create) a new Leak Detection Monitoring configuration.
 */
export const postReseLeakDetectionMonitoringInformation = async (monitoringInfo) => {
    const response = await axios.post(`${ApiPathRESE.baseUrl}/upsertMonitoringDetails`, monitoringInfo, {
        headers: {
            Usecase: "leak-detection",
            Accept: "application/json",
        },
        timeout: 120000,
        timeoutErrorMessage: "request timeout",
    });
    if (response.status >= 400) {
        throw new Error(response.data?.message || response.data?.error || response.statusText);
    }
    return response.data?.message || "Leak Detection Monitoring information submitted successfully";
};
